import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Handshake, Users, ShieldCheck, HandHeart } from "lucide-react";
import { IMAGES } from "@/config/site";
import { Seo } from "@/components/site/Seo";
import { Marquee } from "@/components/site/Marquee";
import { Photo } from "@/components/site/Photo";
import { Reveal, SectionHeading, CtaLink, DonateButton } from "@/components/site/Primitives";
import { HomeHero } from "@/components/home/HomeHero";
import { FloridaDisclosure } from "@/components/site/FloridaDisclosure";
import { LatestNews } from "@/components/home/LatestNews";
import { FRANK_TURNER, CANTEEN_PHOTOS } from "@/content/content";

const SERVES = ["Firefighters", "Law Enforcement Officers", "Emergency Medical Personnel", "Emergency Dispatchers", "Military Veterans"];

const Mission = () => (
  <section className="py-24 md:py-36" data-testid="home-mission-section">
    <div className="container-x grid gap-14 lg:grid-cols-[1.1fr_1fr] lg:gap-24">
      <SectionHeading eyebrow="Our Mission" title="They Answer the Call. We Answer Theirs." className="lg:sticky lg:top-32 lg:self-start" />
      <Reveal delay={0.1} className="space-y-6 text-base leading-relaxed text-ink/85 md:text-lg">
        <p>
          Every day, firefighters, police officers, emergency medical personnel, dispatchers, and veterans dedicate their lives to protecting others. They run toward emergencies, carry the weight of difficult calls, and remain ready long after a shift ends.
        </p>
        <p>
          First Responders Foundation exists to support these individuals in return, through programs that strengthen resilience, promote connection, and build supportive communities around the people who keep South Florida safe.
        </p>
        <ul className="mt-10 border-t border-navy/15">
          {SERVES.map((s, i) => (
            <li key={s} className="group flex items-baseline justify-between border-b border-navy/15 py-4">
              <span className="font-display text-lg font-bold text-navy transition-transform duration-500 group-hover:translate-x-2">{s}</span>
              <span className="font-display text-xs font-semibold tracking-[0.2em] text-gold-deep">0{i + 1}</span>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

const BLOCKS = [
  { t: "Who it serves", d: "Active and retired firefighters, law enforcement officers, EMS personnel, dispatchers, and military veterans in Palm Beach and Broward counties." },
  { t: "How to apply", d: "Apply through this website. The Foundation reviews each application for completeness and basic eligibility, then refers qualifying applicants to Big Dog Ranch Rescue." },
  { t: "Evaluation and training", d: "Big Dog Ranch Rescue makes the final approval decision, evaluates and approves every dog, and oversees training, placement, and follow-up." },
  { t: "How donors help", d: "The program is free to approved participants. Donations help make each partnership between a responder and a dog possible." },
];

const FeaturedProgram = () => (
  <section className="grain relative overflow-hidden bg-navy py-24 text-paper md:py-36" data-testid="home-service-dog-section">
    <div className="container-x grid gap-16 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
      <div className="relative">
        <Photo image={IMAGES.luna} ratio="aspect-[4/5]" testId="home-featured-photo" />
        <Photo image={IMAGES.lunaResting} ratio="aspect-square" className="absolute -bottom-10 -right-4 hidden w-1/2 border-8 border-navy md:block lg:-right-12" />
        <p className="mt-4 text-xs text-[#C9D4DF]">Pictured: Luna, the Foundation's ambassador dog.</p>
      </div>
      <div className="lg:pt-10">
        <SectionHeading light eyebrow="Program 01 · Service Dog Program" title="More Than a Dog. A Lifeline of Support." intro="In partnership with Big Dog Ranch Rescue, our program connects those who serve with carefully evaluated, professionally trained dogs. The bond between a person and a well-matched dog can offer companionship, structure, and a steady presence through the demands of a career in service." />
        <div className="mt-12 grid gap-px bg-white/10 sm:grid-cols-2">
          {BLOCKS.map((b, i) => (
            <Reveal key={b.t} delay={i * 0.08} className="bg-navy p-7">
              <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-gold">{b.t}</p>
              <p className="mt-3 text-sm leading-relaxed text-[#C9D4DF]">{b.d}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 flex flex-col gap-3 sm:flex-row">
          <CtaLink to="/service-dog-program#apply" variant="btn-gold" testId="home-featured-apply-button">Apply for the Program</CtaLink>
          <CtaLink to="/service-dog-program" variant="btn-ghost-light" testId="home-featured-learn-button">Program Details</CtaLink>
        </div>
      </div>
    </div>
  </section>
);

const Canteen = () => (
  <section className="relative overflow-hidden py-24 md:py-36" data-testid="home-canteen-section">
    <span className="outline-num pointer-events-none absolute -right-6 top-10 select-none text-[14rem] md:text-[24rem]" aria-hidden="true">33</span>
    <div className="container-x relative grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
      <div>
        <SectionHeading eyebrow="Program 02 · Canteen 33" title="Supporting Those on the Front Lines." intro="Canteen 33 is the Foundation's mobile canteen and nonmedical rehabilitation support program, serving primarily Palm Beach County. During extended incidents, training events, and emergency operations, Canteen 33 assists requesting agencies with drinking water, electrolyte beverages, coffee, snacks, food, and hot meals when available, so crews can rest, rehydrate, and return to work." />
        <Reveal delay={0.1} className="mt-8 flex items-start gap-4 border-l-2 border-fire bg-white p-6 shadow-[0_4px_20px_rgba(16,38,63,0.06)]">
          <ShieldCheck className="mt-0.5 shrink-0 text-fire" size={22} />
          <p className="text-sm leading-relaxed text-ink">
            <strong className="text-navy">Coordinated, never self-deployed.</strong> Canteen 33 responds only when requested or authorized by an appropriate public safety agency, and every deployment is coordinated with the requesting agency.
          </p>
        </Reveal>
        <div className="mt-10"><CtaLink to="/programs#canteen-33" testId="home-canteen-learn-more-button">Learn More</CtaLink></div>
      </div>
      <Photo image={CANTEEN_PHOTOS[2]} ratio="aspect-[5/4]" showCaption testId="home-canteen-photo" />
    </div>
  </section>
);

const LifelineNote = () => (
  <section className="pb-24 md:pb-32" data-testid="home-lifeline-note">
    <div className="container-x">
      <Reveal className="grid items-center gap-8 border border-navy/10 bg-white p-8 shadow-[0_4px_20px_rgba(16,38,63,0.06)] md:grid-cols-[auto_1fr_auto] md:gap-10 md:p-10">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-navy"><HandHeart className="text-gold" size={30} strokeWidth={1.5} aria-hidden="true" /></span>
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-slate">Program 03 · Lifeline Assistance</p>
            <span className="inline-flex items-center gap-2 rounded-full border border-gold bg-gold/10 px-3 py-1 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-gold-deep" data-testid="home-lifeline-coming-soon"><span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden="true" /> Coming Soon</span>
          </div>
          <h2 className="mt-3 text-xl font-extrabold md:text-2xl">First Responders Lifeline Assistance Program</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/80 md:text-base">We are developing a program to provide short-term emergency financial assistance to eligible first responders and their families facing hardship from serious illness, injury, or death. The program is under development and is not yet accepting applications or providing assistance.</p>
        </div>
        <CtaLink to="/programs#lifeline-assistance" variant="btn-ghost" testId="home-lifeline-learn-more">Learn More</CtaLink>
      </Reveal>
    </div>
  </section>
);

const WAYS = [
  { icon: Heart, t: "Donate", d: "Support the Foundation's mission and programs with a one-time or monthly gift.", to: null, cls: "bg-navy text-paper lg:row-span-2", id: "donate" },
  { icon: Handshake, t: "Sponsor", d: "Become a community or corporate partner and help sustain programs for those who serve.", to: "/get-involved#sponsor", cls: "bg-white", id: "sponsor" },
  { icon: Users, t: "Volunteer", d: "Help support first responders and veterans at events, deployments, and behind the scenes.", to: "/get-involved#volunteer", cls: "bg-mist", id: "volunteer" },
];

const WayCard = ({ w }) => {
  const Icon = w.icon;
  const dark = w.id === "donate";
  const body = (
    <>
      <Icon size={30} className={dark ? "text-gold" : "text-navy"} />
      <div className="mt-auto pt-12">
        <h3 className={`text-3xl font-extrabold md:text-4xl ${dark ? "text-paper" : "text-navy"}`}>{w.t}</h3>
        <p className={`mt-3 max-w-sm text-sm leading-relaxed md:text-base ${dark ? "text-[#C9D4DF]" : "text-ink/80"}`}>{w.d}</p>
      </div>
      <ArrowUpRight className={`absolute right-7 top-7 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 ${dark ? "text-gold" : "text-navy"}`} />
    </>
  );
  const cls = `group relative flex h-full min-h-[260px] flex-col p-8 md:p-10 transition-shadow duration-500 hover:shadow-[0_20px_50px_rgba(16,38,63,0.15)] ${w.cls}`;
  return w.to ? (
    <Link to={w.to} className={cls} data-testid={`ways-${w.id}-card`}>{body}</Link>
  ) : (
    <div className={cls} data-testid={`ways-${w.id}-card`}>
      {body}
      <DonateButton label="Donate Now" variant="btn-gold" className="mt-8 w-fit" testId="ways-donate-button" />
    </div>
  );
};

const Ways = () => (
  <section className="bg-paper pb-24 md:pb-36" data-testid="home-ways-section">
    <div className="container-x">
      <SectionHeading eyebrow="Get Involved" title="Ways to Make a Difference" />
      <div className="mt-14 grid gap-5 lg:grid-cols-2 lg:grid-rows-2">
        {WAYS.map((w, i) => (
          <Reveal key={w.id} delay={i * 0.1} className={i === 0 ? "lg:row-span-2" : ""}><WayCard w={w} /></Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Stories = () => (
  <section className="border-t border-navy/10 bg-white py-24 md:py-36" data-testid="home-impact-section">
    <div className="container-x grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:items-end lg:gap-20">
      <div>
        <SectionHeading eyebrow="In Memoriam" title={FRANK_TURNER.title} />
        <Reveal delay={0.1} className="mt-6 max-w-2xl text-base leading-relaxed text-ink/80 md:text-lg">
          <p>For more than 20 years, Frank Turner volunteered as Santa Claus aboard a historic 1961 ladder truck, bringing Christmas joy to children and families, including children in hospitals. When Frank was diagnosed with ALS, our community came together to support Frank and his wife, Becky.</p>
        </Reveal>
        <Reveal delay={0.15} className="mt-10"><CtaLink to="/impact#frank-turner" variant="btn-ghost" testId="home-frank-turner-button">Read Frank's Story</CtaLink></Reveal>
      </div>
      <Reveal delay={0.1} className="grid grid-cols-3 gap-px border border-navy/10 bg-navy/10">
        {FRANK_TURNER.facts.map((f) => (
          <div key={f.k} className="bg-paper p-5 md:p-6">
            <p className="font-display text-xl font-black text-navy md:text-2xl">{f.v}</p>
            <p className="mt-2 text-xs font-semibold leading-snug text-slate">{f.k}</p>
          </div>
        ))}
      </Reveal>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="grain relative overflow-hidden bg-navy py-24 text-paper md:py-32" data-testid="home-final-cta-section">
    <div className="container-x grid items-end gap-10 lg:grid-cols-[1.4fr_1fr]">
      <SectionHeading light eyebrow="Donate" title="Your Support Can Change Lives." intro="A gift to First Responders Foundation helps sustain the programs that stand beside those who stand between our communities and harm. Whether you give once or every month, your generosity becomes evaluations, training, nourishment on long nights, and a community that shows up for its responders." />
      <Reveal delay={0.15} className="flex flex-col gap-3 lg:items-end">
        <DonateButton label="Donate Now" className="!px-10 !py-5 text-sm" testId="home-final-donate-button" />
        <CtaLink to="/get-involved#monthly" variant="btn-ghost-light" testId="home-final-monthly-button">Give Monthly</CtaLink>
      </Reveal>
      <FloridaDisclosure dark className="max-w-4xl lg:col-span-2" testId="home-florida-disclosure" />
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo title="First Responders Foundation | Service Dogs for First Responders, South Florida" description="South Florida nonprofit supporting firefighters, law enforcement, EMS, dispatchers, and veterans through a Service Dog Program and Canteen 33 responder support." path="/" />
      <HomeHero />
      <Marquee />
      <Mission />
      <FeaturedProgram />
      <Canteen />
      <LifelineNote />
      <LatestNews />
      <Ways />
      <Stories />
      <FinalCta />
    </>
  );
}
